<!-- ─── Trigger button – only visible when reservation or homeowner context is active ── -->
@if (showButton) {
  <div class="email-dir-trigger-wrap">
    <button
      pButton
      pRipple
      type="button"
      class="email-dir-btn p-button-outlined p-button-sm"
      (click)="togglePanel($event)"
      pTooltip="Email Directory"
      tooltipPosition="bottom">
      <i class="pi pi-address-book"></i>
      <span class="email-dir-label">Email Directory</span>
    </button>

    <!-- ─── Overlay panel ──────────────────────────────────────────────────── -->
    <p-overlayPanel
      #directoryPanel
      [showCloseIcon]="true"
      [dismissable]="true"
      [style]="{ width: '460px', padding: '0' }"
      (onHide)="resetPanel()">

      <!-- Panel header -->
      <div class="dir-panel-header">
        <i class="pi pi-address-book dir-header-icon"></i>
        <span class="dir-header-title">Email Directory</span>
      </div>

      <!-- ─── Mode tabs (only when both contexts are selected) ─────────────── -->
      @if (showBothModes) {
        <div class="dir-mode-tabs">
          <button
            class="dir-mode-tab"
            [class.active]="activeMode === 'reservation'"
            (click)="switchMode('reservation')">
            <i class="pi pi-calendar"></i> Reservation
          </button>
          <button
            class="dir-mode-tab"
            [class.active]="activeMode === 'homeowner'"
            (click)="switchMode('homeowner')">
            <i class="pi pi-home"></i> Homeowner
          </button>
        </div>
      }

      <!-- ══════════════════════════════════════════════════════════════════ -->
      <!-- RESERVATION MODE                                                   -->
      <!-- ══════════════════════════════════════════════════════════════════ -->
      @if (isReservationMode) {
        <div class="dir-section">

          @if (!showBothModes) {
            <div class="dir-section-header">
              <i class="pi pi-calendar"></i>
              <span>Find by Reservation</span>
            </div>
          }

          <div class="dir-body">
            <!-- Reuse the existing reservation-select autocomplete component -->
            <app-reservation-select
              #reservationSelector
              placeholder="Search by Reservation ID, guest name or property…"
              (reservationInfoEvent)="onReservationSelected($event)">
            </app-reservation-select>

            <!-- Result card after a reservation is selected -->
            @if (selectedReservation) {
              <div class="dir-result-card">
                <div class="dir-result-info">
                  <span class="dir-result-name">
                    {{ selectedReservation?.firstName }}
                    {{ selectedReservation?.lastName }}
                  </span>
                  <span class="dir-result-email">
                    {{ selectedReservation?.emailAddress || '— no email —' }}
                  </span>
                  <span class="dir-result-email">
                    {{ selectedReservation?.checkInDateTime | date:'MM/dd/yy' }} - {{ selectedReservation?.checkOutDateTime | date:'MM/dd/yy' }}
                  </span>
                  <span class="dir-result-meta">
                    {{ selectedReservation?.propertyCode }} ·
                    Res&nbsp;#{{ selectedReservation?.reservationId }}
                  </span>
                </div>

                <button
                  pButton pRipple
                  type="button"
                  class="p-button-sm dir-compose-btn"
                  label="Compose"
                  icon="pi pi-send"
                  [disabled]="!canComposeForReservation"
                  (click)="composeForReservation()">
                </button>
              </div>

              @if (!canComposeForReservation) {
                <div class="dir-warn">
                  <i class="pi pi-exclamation-triangle"></i>
                  This guest has no email address for reservation.
                </div>
              }
            }
          </div>
        </div>
      }

      <!-- ══════════════════════════════════════════════════════════════════ -->
      <!-- HOMEOWNER MODE                                                     -->
      <!-- ══════════════════════════════════════════════════════════════════ -->
      @if (isHomeownerMode) {
        <div class="dir-section">

          @if (!showBothModes) {
            <div class="dir-section-header">
              <i class="pi pi-home"></i>
              <span>Find Homeowners</span>
            </div>
          }

          <div class="dir-body">

            <!-- Search-mode toggle -->
            <div class="dir-toggle-wrap">
              <p-selectButton
                [options]="searchModeOptions"
                [(ngModel)]="ownerSearchMode"
                optionLabel="label"
                optionValue="value"
                (onChange)="onSearchModeChange()"
                styleClass="dir-search-toggle">
              </p-selectButton>
            </div>

            <!-- ── By Owner Name ────────────────────────────────────────── -->
            @if (ownerSearchMode === 'name') {
              <div class="dir-search-row">
                <span class="p-input-icon-right w-100">
                  <i class="pi pi-search"></i>
                  <input
                    pInputText
                    type="text"
                    class="w-100"
                    placeholder="Search owner by name, email, or phone..."
                    [(ngModel)]="ownerNameQuery"
                    (input)="onOwnerNameInput(ownerNameQuery)" />
                </span>
              </div>

              @if (isLoading) {
                <div class="dir-loading">
                  <i class="pi pi-spin pi-spinner"></i> Searching…
                </div>
              }

              @if (!isLoading && ownerNameQuery && ownerResults.length === 0) {
                <div class="dir-empty">
                  <i class="pi pi-inbox"></i> No owners found.
                </div>
              }

              @if (!isLoading && ownerResults.length > 0) {
                <div class="dir-results-list">
                  @for (owner of ownerResults; track owner.ownerProfileId) {
                    <div class="dir-result-row">
                      <p-checkbox
                        [(ngModel)]="selectedOwners"
                        [value]="owner"
                        [inputId]="'own_n_' + owner.ownerProfileId">
                      </p-checkbox>
                      <label
                        [for]="'own_n_' + owner.ownerProfileId"
                        class="dir-owner-label">
                        <span class="dir-result-name">
                          {{ owner?.firstName }} {{ owner?.lastName }}
                        </span>
                        <span class="dir-result-email">{{ owner?.emailAddress }}</span>
                        <span class="dir-result-email">{{ owner?.mobilePhone }}</span>
                      </label>
                    </div>
                  }
                </div>
              }
            }

            <!-- ── By Property Code ─────────────────────────────────────── -->
            @if (ownerSearchMode === 'property') {
              <div class="dir-search-row">
                <p-autoComplete
                  [(ngModel)]="selectedProperty"
                  [suggestions]="propertySuggestions"
                  field="propertyCode"
                  placeholder="Search property code…"
                  [style]="{ width: '100%' }"
                  [inputStyle]="{ width: '100%' }"
                  [minLength]="1"
                  (completeMethod)="onPropertySearch($event)"
                  (onSelect)="onPropertySelected($event)">
                  <ng-template pTemplate="item" let-prop>
                    <div class="dir-prop-item">
                      <span class="dir-prop-code">{{ prop.propertyCode }}</span>
                      <span class="dir-prop-name">{{ prop.shortTermRentalName }}</span>
                    </div>
                  </ng-template>
                </p-autoComplete>
              </div>

              @if (isLoading) {
                <div class="dir-loading">
                  <i class="pi pi-spin pi-spinner"></i> Loading owners…
                </div>
              }

              @if (!isLoading && selectedProperty && propertyOwners.length === 0) {
                <div class="dir-empty">
                  <i class="pi pi-inbox"></i> No owners with email found for this property.
                </div>
              }

              @if (!isLoading && propertyOwners.length > 0) {
                <div class="dir-results-list">
                  @for (owner of propertyOwners; track owner.ownerProfileId) {
                    <div class="dir-result-row">
                      <p-checkbox
                        [(ngModel)]="selectedOwners"
                        [value]="owner"
                        [inputId]="'own_p_' + owner.ownerProfileId">
                      </p-checkbox>
                      <label
                        [for]="'own_p_' + owner.ownerProfileId"
                        class="dir-owner-label">
                        <span class="dir-result-name">
                          {{ owner.lastName }}, {{ owner.firstName }}
                        </span>
                        <span class="dir-result-email">{{ owner.emailAddress }}</span>
                      </label>
                    </div>
                  }
                </div>
              }
            }

            <!-- ── Compose footer (shown when at least one owner is checked) ── -->
            @if (canComposeForOwners) {
              <div class="dir-compose-footer">
                <span class="dir-selected-count">
                  <i class="pi pi-users"></i>
                  {{ selectedOwners.length }} owner{{ selectedOwners.length !== 1 ? 's' : '' }} selected
                </span>
                <button
                  pButton pRipple
                  type="button"
                  class="p-button-sm dir-compose-btn"
                  label="Compose"
                  icon="pi pi-send"
                  (click)="composeForOwners()">
                </button>
              </div>
            }

          </div>
        </div>
      }

    </p-overlayPanel>
  </div>
}
